/**
 * Defines a React hook that simulates a typewriter effect for a given string.
 * This hook provides a purely presentational effect for the UI. It uses `setInterval` and `useState`
 * to gradually reveal a string of text character by character, mimicking a typing animation.
 *
 * It is well-encapsulated and manages its own timer lifecycle correctly using `useEffect` for setup
 * and cleanup. It also provides a `forceComplete` function to allow skipping the animation.
 *
 * @note This hook is currently implemented and tested, but it is disabled (commented out) in its only
 * intended usage location, `client/chat/components/ChatMessagesView.tsx`. It can be considered an inactive feature
 * that is ready for activation.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Options to configure the typing effect.
 */
interface UseTypingEffectOptions {
  /** The speed of the typing animation in characters per second. */
  speed?: number;
  /** Pause duration in milliseconds when a newline is encountered. Defaults to 300ms. */
  newlinePauseMs?: number;
  /** A callback function to execute when the typing animation is complete. */
  onComplete?: () => void;
}

/**
 * A custom hook that creates a typewriter effect for a given string.
 *
 * @param text - The full text to be displayed.
 * @param isActive - A flag to start or stop the typing effect.
 * @param [options={}] - Configuration options for the effect.
 * @returns {{
 *   displayedText: string;
 *   isTyping: boolean;
 *   forceComplete: () => void;
 * }} An object containing the currently displayed text, a boolean indicating if the animation is running,
 * and a function to immediately complete the animation.
 */
export function useTypingEffect(text: string, isActive: boolean, options: UseTypingEffectOptions = {}) {
  const { speed = 30, newlinePauseMs = 500, onComplete } = options;
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const textQueueRef = useRef<string>('');
  const currentIndexRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  // Schedule the next character with variable delay
  const scheduleNext = useRef<() => void>(undefined);
  scheduleNext.current = () => {
    const msPerChar = 1000 / speed;

    if (currentIndexRef.current >= textQueueRef.current.length) {
      // Typing is complete.
      timerRef.current = null;
      setIsTyping(false);
      setDisplayedText(textQueueRef.current);
      onComplete?.();
      return;
    }

    if (currentIndexRef.current === 0) {
      setIsTyping(true);
    }

    const nextChar = textQueueRef.current[currentIndexRef.current]!;
    currentIndexRef.current++;
    setDisplayedText((prev) => prev + nextChar);

    // Pause before a double newline (\n\n) block — the delay applies after
    // typing the character that precedes the blank line, so the newlines
    // appear together after the pause.
    const nextIsDoubleNewline =
      textQueueRef.current[currentIndexRef.current] === '\n' &&
      textQueueRef.current[currentIndexRef.current + 1] === '\n';
    const delay = nextIsDoubleNewline ? newlinePauseMs : msPerChar;
    timerRef.current = setTimeout(() => scheduleNext.current?.(), delay);
  };

  // The main effect that manages the typing lifecycle.
  useEffect(() => {
    if (!isActive) {
      setDisplayedText('');
      setIsTyping(false);
      textQueueRef.current = '';
      currentIndexRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    textQueueRef.current = text;

    // Skip animation entirely when the user prefers reduced motion.
    if (prefersReducedMotion.current) {
      setDisplayedText(text);
      currentIndexRef.current = text.length;
      setIsTyping(false);
      if (text.length > 0) onComplete?.();
      return;
    }

    if (!timerRef.current && text.length > 0) {
      scheduleNext.current?.();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [text, isActive, speed, newlinePauseMs, onComplete]);

  const forceComplete = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setDisplayedText(textQueueRef.current);
    currentIndexRef.current = textQueueRef.current.length;
    setIsTyping(false);
    onComplete?.();
  };

  return { displayedText, isTyping, forceComplete };
}
